import { useEffect, useState } from "react";
import { Timer } from "lucide-react";

const fmt = (n) => String(n).padStart(2, "0");

const Unit = ({ value, label, color }) => (
  <div className="flex flex-col items-center px-3 sm:px-5 py-3">
    <div className="font-display font-black italic text-4xl sm:text-6xl leading-none tabular-nums" style={{ color }}>
      {fmt(value)}
    </div>
    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-2">{label}</div>
  </div>
);

function diff(targetIso) {
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return null;
  const now = Date.now();
  const ms = target - now;
  if (ms <= 0) return { done: true, days: 0, hours: 0, mins: 0, secs: 0 };
  const days  = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins  = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const secs  = Math.floor((ms % (1000 * 60)) / 1000);
  return { done: false, days, hours, mins, secs };
}

export default function Countdown({ title, target, url, themeColor = "#00E5FF", centered = false }) {
  const [t, setT] = useState(() => diff(target));
  useEffect(() => {
    setT(diff(target));
    const i = setInterval(() => setT(diff(target)), 1000);
    return () => clearInterval(i);
  }, [target]);

  if (!t) return null;

  const isExternal = !!url && /^https?:\/\//i.test(url);
  const Wrapper = url
    ? (props) => (
        <a
          href={url}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noreferrer" : undefined}
          className="block hover:brightness-110 transition"
          {...props}
        />
      )
    : "div";

  const timerBlock = !t.done ? (
    <div className="flex items-stretch divide-x divide-white/10 border border-white/10 bg-[#0B0F19]">
      <Unit value={t.days} label="Tage" color={themeColor} />
      <Unit value={t.hours} label="Std" color={themeColor} />
      <Unit value={t.mins} label="Min" color={themeColor} />
      <Unit value={t.secs} label="Sek" color={themeColor} />
    </div>
  ) : (
    <div className="text-emerald-400 font-display font-black italic uppercase text-2xl">Jetzt live</div>
  );

  if (centered) {
    return (
      <Wrapper>
        <section
          className="relative border border-white/10 bg-[#0F1419] overflow-hidden"
          data-testid="home-countdown"
        >
          <div
            className="absolute inset-0 opacity-10"
            style={{ background: `radial-gradient(circle at center, ${themeColor}, transparent 70%)` }}
          />
          <div className="relative px-6 md:px-10 py-8 md:py-10 flex flex-col items-center justify-center gap-5">
            <div className="flex items-center gap-2" style={{ color: themeColor }}>
              <Timer className="w-4 h-4" />
              <span className="eyebrow">Countdown</span>
            </div>
            {timerBlock}
          </div>
        </section>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <section
        className="relative border border-white/10 bg-[#0F1419] overflow-hidden"
        data-testid="home-countdown"
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{ background: `radial-gradient(circle at top right, ${themeColor}, transparent 60%)` }}
        />
        <div className="relative px-6 md:px-10 py-6 md:py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#00E5FF]">
              <Timer className="w-4 h-4" />
              <span className="eyebrow">Countdown</span>
            </div>
            <div className="font-display font-black italic uppercase text-2xl sm:text-3xl mt-1 leading-tight">
              {t.done ? "Es ist soweit!" : (title || "Bald geht es los")}
            </div>
          </div>
          {timerBlock}
        </div>
      </section>
    </Wrapper>
  );
}
